// 2D (/game) vs 3D (/game3d) view choice for a game session.
//
// Kept out of the lazy /game3d chunk on purpose: FeaturedCaseEntry and
// GamePage both decide on it, and the only 3D module it touches is the scene
// registry, which is plain data (no three.js / Spark).
import { caseHasScenes } from '../components/game3d/sceneRegistry';

export const VIEW_MODE_STORAGE_KEY = 'myshaps-view-mode';
export const VIEW_MODE_2D = '2d';
export const VIEW_MODE_3D = '3d';

/**
 * The player's remembered view on this device: '2d', '3d', or null when there
 * is none. Unreadable storage (private mode, blocked site data) is "none".
 */
export const readViewPreference = () => {
  try {
    const value = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return value === VIEW_MODE_2D || value === VIEW_MODE_3D ? value : null;
  } catch {
    return null;
  }
};

/** Remember the player's view choice. A storage failure is ignored. */
export const writeViewPreference = (mode) => {
  if (mode !== VIEW_MODE_2D && mode !== VIEW_MODE_3D) return;
  try {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // No preference is stored; the default route applies next time.
  }
};

const matches = (query) => {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && Boolean(window.matchMedia(query)?.matches);
  } catch {
    return false;
  }
};

// WebGL2 support does not change during a page's life, so it is probed once.
let webglAvailable = null;

/**
 * Whether the browser can create a WebGL2 context. WebGL2 only: the pinned
 * three.js dropped WebGL 1 in r163, so a WebGL1-only device cannot render the
 * room. Probed once per page and cached.
 */
export const hasWebGL2 = () => {
  if (webglAvailable !== null) return webglAvailable;
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    webglAvailable = Boolean(gl);
    // Hand the probe's context straight back: browsers cap live contexts.
    gl?.getExtension?.('WEBGL_lose_context')?.loseContext();
  } catch {
    webglAvailable = false;
  }
  return webglAvailable;
};

/** Test-only: forget the cached WebGL2 probe result. */
export const resetWebGLProbeForTests = () => {
  webglAvailable = null;
};

/**
 * Whether this device can use the 3D view: a desktop-sized window with a fine
 * pointer (mouse/trackpad, so touch-first tablets are excluded) and WebGL2.
 * Evaluated at call time; no `matchMedia` (e.g. jsdom) means not capable.
 */
export const canUse3D = () => (
  matches('(min-width: 1024px)')
  && matches('(pointer: fine)')
  && hasWebGL2()
);

/**
 * Where a featured case opens: '/game3d' when its locations have 3D scenes,
 * the device can use them and the player has not chosen 2D; '/game' otherwise.
 *
 * @param {object[]} locations - the case's location roster (`id`, `image`)
 * @returns {'/game3d' | '/game'}
 */
export const pickFeaturedRoute = (locations) => {
  if (!caseHasScenes(locations)) return '/game';
  if (readViewPreference() === VIEW_MODE_2D) return '/game';
  return canUse3D() ? '/game3d' : '/game';
};
